import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Check, Coins, Sparkles, Loader2 } from "lucide-react";

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function BuyCredits() {
  const { user, setUser } = useAuth();
  const [packs, setPacks] = useState([]);
  const [keyId, setKeyId] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    api.get("/credits/packs").then(({ data }) => { setPacks(data.packs); setKeyId(data.razorpay_key_id); }).catch(() => {});
  }, []);

  const buy = async (pack) => {
    setBusy(pack.id);
    const ok = await loadRazorpay();
    if (!ok) { toast.error("Could not load payment gateway."); setBusy(null); return; }

    try {
      const { data: order } = await api.post("/payments/create-order", { pack_id: pack.id });
      const rzp = new window.Razorpay({
        key: order.razorpay_key_id || keyId,
        amount: order.amount,
        currency: order.currency,
        order_id: order.order_id,
        name: "MainsMentor",
        description: `${pack.name} — ${pack.credits} evaluations`,
        prefill: { name: user?.name || "", email: user?.email || "" },
        theme: { color: "#2D6A4F" },
        handler: async (resp) => {
          try {
            const { data } = await api.post("/payments/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            setUser((u) => (u ? { ...u, credits: data.credits } : u));
            toast.success(`Added ${data.added} credits! You now have ${data.credits}.`);
          } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Verification failed");
          } finally {
            setBusy(null);
          }
        },
        modal: { ondismiss: () => setBusy(null) },
      });
      rzp.on("payment.failed", () => { toast.error("Payment failed. Please try again."); setBusy(null); });
      rzp.open();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      setBusy(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFDF9]">
      <Navbar />
      <main className="max-w-5xl mx-auto px-5 py-10">
        <div className="text-center max-w-2xl mx-auto">
          <span className="text-xs uppercase tracking-[0.2em] text-[#2D6A4F] font-semibold">Credits</span>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight mt-2">Buy evaluation credits</h1>
          <p className="text-[#525252] mt-2">1 credit = 1 full mentor evaluation. Pay per answer, or save more with a bundle — no subscription.</p>
          <div className="inline-flex items-center gap-2 mt-5 bg-[#FBEEE8] text-[#B0452A] border border-[#F3D9CE] rounded-full px-4 py-1.5 text-sm font-semibold">
            <Coins className="w-4 h-4" /> You currently have {user?.credits ?? 0} credits
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
          {packs.map((p) => {
            const popular = p.id === "value";
            const singlePack = packs.find((x) => x.id === "single");
            const singlePerCredit = singlePack ? singlePack.price / singlePack.credits : null;
            const perCredit = p.price / p.credits;
            const savePct = singlePerCredit && p.id !== "single"
              ? Math.round((1 - perCredit / singlePerCredit) * 100) : 0;
            return (
              <div
                key={p.id}
                data-testid={`pack-${p.id}`}
                className={`relative rounded-3xl border p-7 flex flex-col ${popular ? "border-[#2D6A4F] bg-white shadow-lg lg:-translate-y-2" : "border-[#E5E5E5] bg-white"}`}
              >
                {popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 bg-[#2D6A4F] text-white text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap">
                    <Sparkles className="w-3 h-3" /> Most popular
                  </span>
                )}
                <span className="text-xs uppercase tracking-[0.2em] text-[#525252] font-semibold">{p.name}</span>
                <div className="mt-3 flex items-end gap-1">
                  <span className="font-display text-5xl text-[#1C1C1C]">₹{(p.price / 100).toFixed(0)}</span>
                </div>
                <div className="mt-1 flex items-center gap-2 flex-wrap">
                  <span className="text-[#525252]">₹{(p.price / 100 / p.credits).toFixed(p.credits === 1 ? 0 : 1)} per evaluation</span>
                  {savePct > 0 && (
                    <span className="inline-flex items-center bg-[#FBEEE8] text-[#B0452A] text-xs font-semibold px-2 py-0.5 rounded-full" data-testid={`save-${p.id}`}>
                      Save {savePct}%
                    </span>
                  )}
                </div>

                <div className="mt-6 space-y-3 flex-1">
                  <div className="flex items-center gap-2"><Check className="w-4 h-4 text-[#2D6A4F]" /> <span className="font-semibold">{p.credits} {p.credits === 1 ? "evaluation" : "evaluations"}</span></div>
                  <div className="flex items-center gap-2 text-[#525252]"><Check className="w-4 h-4 text-[#2D6A4F]" /> Full mentor feedback</div>
                  <div className="flex items-center gap-2 text-[#525252]"><Check className="w-4 h-4 text-[#2D6A4F]" /> Model answer + topper comparison</div>
                  <div className="flex items-center gap-2 text-[#525252]"><Check className="w-4 h-4 text-[#2D6A4F]" /> Credits never expire</div>
                </div>

                <Button
                  data-testid={`buy-${p.id}-btn`}
                  onClick={() => buy(p)}
                  disabled={busy !== null}
                  className={`mt-8 w-full h-12 rounded-full text-base ${popular ? "bg-[#2D6A4F] hover:bg-[#1B4332] text-white" : "bg-[#1C1C1C] hover:bg-[#333] text-white"}`}
                >
                  {busy === p.id ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing…</> : (p.id === "single" ? "Buy 1 credit" : "Buy now")}
                </Button>
              </div>
            );
          })}
        </div>

        <p className="text-center text-sm text-[#525252] mt-8">
          Secure payments via Razorpay. Credits are added instantly after a successful payment.
        </p>
      </main>
    </div>
  );
}
